import { Fragment, useEffect, useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ConcertImage } from '../components/ConcertImage.tsx'
import { DiceControls } from '../components/DiceControls.tsx'
import { NoteEditor } from '../components/NoteEditor.tsx'
import { ShareSheet } from '../components/ShareSheet.tsx'
import { SharedAnnotationList } from '../components/SharedAnnotation.tsx'
import { ThumbControls } from '../components/ThumbControls.tsx'
import { stageColor } from '../components/stageColor.ts'
import { simultaneousConcerts } from '../domain/overlap.ts'
import { findConcert } from '../domain/program.ts'
import { concertStatus, formatDayLabel, formatTimeRange } from '../domain/time.ts'
import { useNow } from '../hooks/useNow.ts'
import { t } from '../i18n.ts'
import { CONCERTS } from '../program.ts'
import {
  useAnnotation,
  useSharedAnnotations,
  useStorageMode,
  useStore,
} from '../store/StoreProvider.tsx'
import { NotFound } from './NotFound.tsx'
import './ConcertDetail.css'

export function ConcertDetail() {
  const { id = '' } = useParams()
  const concert = findConcert(CONCERTS, id)
  const store = useStore()
  const annotation = useAnnotation(id)
  const storageMode = useStorageMode()
  const shared = useSharedAnnotations(id)
  const now = useNow()

  /*
   * A concert page opens at the top.
   *
   * Client-side routing changes the document without touching the scroll position,
   * so tapping the last row of a scrolled-down schedule would otherwise land the
   * reader halfway through the bio of a page they have never seen. The body is the
   * scrolling element — `.app-main` has no `overflow` of its own — so `window` is
   * what has to move.
   *
   * Keyed on the id rather than on mount: React reuses this component when one
   * concert page links to another, and that is exactly the case where arriving
   * mid-page would be most confusing.
   */
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [id])

  /*
   * Memoised because this page re-renders on the minute tick and on any store write
   * anywhere in the app, while the answer depends only on which concert is shown.
   */
  const simultaneous = useMemo(
    () => (concert ? simultaneousConcerts(CONCERTS, concert) : []),
    [concert],
  )

  if (!concert) return <NotFound />

  const status = concertStatus(concert, now)

  return (
    <article
      className="concert page"
      // Per-concert accent, taken from the artwork. Detail view only — the day list
      // stays visually calm so 22 rows do not turn into a colour chart.
      style={concert.accent ? ({ '--concert-accent': concert.accent } as React.CSSProperties) : undefined}
    >
      <ConcertImage concert={concert} />

      <header className="concert__head">
        <h1 className="concert__name">{concert.name}</h1>

        <p className="concert__when">
          <span className="concert__day">{formatDayLabel(concert.day)}</span>
          {/*
            The time doubles as the way back into the schedule at this moment —
            "what else is on around now" is the question a concert page most often
            provokes. Inline, because it sits inside a paragraph of metadata.
          */}
          <Link to={`/dag/${concert.day}?tid=${concert.start}`} className="concert__time-link">
            <span className="concert__time">{formatTimeRange(concert)}</span>
            <span className="sr-only">, {t.concert.timeLink}</span>
          </Link>
          <span className="concert__stage">
            <span
              className="concert__stage-dot"
              style={{ background: stageColor(concert.stage) }}
              aria-hidden="true"
            />
            {concert.stage}
          </span>
        </p>

        {/*
          What you give up by being here, right under the time that says when. Five
          concerts in the programme genuinely clash with nothing, and they get no line
          at all rather than an empty label — «Samtidig som» followed by nothing reads
          as a bug.
        */}
        {simultaneous.length > 0 && (
          <p className="concert__simultaneous">
            {t.concert.simultaneous}{' '}
            {simultaneous.map((other, index) => (
              <Fragment key={other.id}>
                {index > 0 && ', '}
                <Link to={`/konsert/${other.id}`} className="concert__simultaneous-link">
                  {other.name}
                </Link>
              </Fragment>
            ))}
          </p>
        )}

        {status === 'paagaar' && <p className="concert__live">{t.day.live}</p>}

        {concert.genres.length > 0 && (
          <ul className="concert__genres" aria-label={t.concert.genres}>
            {concert.genres.map((genre) => (
              <li key={genre} className="concert__genre">
                {genre}
              </li>
            ))}
          </ul>
        )}
      </header>

      <SharedAnnotationList shared={shared} />

      {/*
        The rating stays up top: it is one tap, and it is what most people come to
        the page to do. Writing a note and sharing are deliberate acts that follow
        reading the bio, so they sit below it rather than between it and the top.
      */}
      <section className="concert__annotate">
        <ThumbControls
          vote={annotation?.vote ?? null}
          onChange={(vote) => store.setVote(id, vote)}
        />
      </section>

      <section className="concert__text">
        {concert.ingress && <p className="concert__ingress">{concert.ingress}</p>}
        {concert.bio.map((paragraph, index) => (
          <p key={index}>{paragraph}</p>
        ))}
      </section>

      {/*
        The dice belong here rather than beside the thumbs up top. A thumb is a plan —
        «jeg vil se denne» — and it is set before the concert; a terningkast is a verdict,
        set afterwards, in the same sitting as writing the note about it. Keeping the die
        and the note in one block means one visit down here after the concert, not two.
      */}
      <section className="concert__note">
        <DiceControls
          dice={annotation?.dice ?? null}
          onChange={(dice) => store.setDice(id, dice)}
        />

        <NoteEditor
          value={annotation?.note ?? ''}
          onSave={(note) => store.setNote(id, note)}
          storageMode={storageMode}
        />
      </section>

      <ShareSheet concert={concert} />
    </article>
  )
}
